import { getSupabase } from './supabase';
import { markAuthPending, clearAuthPending } from '@/lib/authPending';

export interface AuthUserProfile {
  id: string;
  email: string;
  displayName: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuthEntitlement {
  id: string;
  plan: string;
  maxProjects: number;
  seats: number;
  status: string;
  renewsAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AuthSession {
  user: AuthUserProfile;
  entitlement: AuthEntitlement | null;
  defaultProjectId: string;
}

export type AuthUser = AuthUserProfile;

export async function signInWithGoogle() {
  markAuthPending();
  try {
    // Save intended return location only if internal route
    const href = typeof window !== 'undefined' ? window.location.pathname + window.location.search + window.location.hash : '';
    if (href && href.startsWith('/') && href !== '/') localStorage.setItem('returnTo', href);
  } catch {}
  const APP_URL = ((import.meta as any).env?.VITE_APP_URL as string) || (typeof window !== 'undefined' ? window.location.origin : 'https://lumiplot.ai');
  const { error } = await getSupabase().auth.signInWithOAuth({
    provider: 'google',
    options: {
      // Land users in the editor after Google OAuth completes
      redirectTo: `${APP_URL.replace(/\/$/, '')}/plot-editor/new`,
    },
  });
  
  if (error) { clearAuthPending(); throw error; }
}

export async function signInWithMagicLink(email: string) {
  markAuthPending();
  try {
    const href = typeof window !== 'undefined' ? window.location.pathname + window.location.search + window.location.hash : '';
    if (href && href.startsWith('/') && href !== '/') localStorage.setItem('returnTo', href);
  } catch {}
  const APP_URL = ((import.meta as any).env?.VITE_APP_URL as string) || (typeof window !== 'undefined' ? window.location.origin : 'https://lumiplot.ai');
  const { error } = await getSupabase().auth.signInWithOtp({
    email,
    options: {
      // Preserve current path + search so the magic link returns to the initiating route
      emailRedirectTo: `${APP_URL.replace(/\/$/, '')}${typeof window !== 'undefined' ? (window.location.pathname + window.location.search) : ''}`,
    },
  });
  
  if (error) { clearAuthPending(); throw error; }
}

export async function signOut(scope: 'global' | 'local' = 'global') {
  const { error } = await getSupabase().auth.signOut(scope === 'local' ? { scope: 'local' } : undefined);
  if (error) throw error;
}

export async function getCurrentSession(): Promise<AuthSession | null> {
  try {
    const { data, error } = await getSupabase().auth.getSession();
    if (error) {
      console.error('Failed to get Supabase session', error);
      throw error;
    }

    const token = data.session?.access_token;
    if (!token) {
      return null;
    }

    const response = await fetch('/api/auth/session', {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.status === 401) {
      return null;
    }

    await throwIfResNotOk(response);
    const payload = await response.json();
    return payload as AuthSession;
  } catch (error) {
    console.error('Failed to load auth session', error);
    return null;
  }
}

export function onAuthStateChange(callback: (user: any) => void) {
  return getSupabase().auth.onAuthStateChange((_event, session) => {
    callback(session?.user ?? null);
  });
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}
